import {
  DataScienceModelMap,
  IncivilityValue,
  ModelConfigs,
  ModelConfigsValues,
  ModelConfigValue,
  ModerationFiltersConfigsResponse,
  ModerationModel,
  MultipleThresholds,
  SingleThreshold,
  TenantConfigs,
  TenantConfigValue,
  Thresholds
} from './moderationFiltersTypes';
import { AnyTag, Tag } from '../../../../moderation-v2/comments-and-posts/state/types';
import { getTagText } from '../../../../moderation-v2/comments-and-posts/dashboard/header/tag-tooltip';
import {
  DEFAULT_SINGLE_THRESHOLD,
  INAPPROPRIATE_USERNAME_MODEL,
  INCIVILITY_TAGGER_FEATURE_FLAG,
  INCIVILITY_THRESHOLD_CONFIG,
  MEDIUM_NUM_THRESHOLD,
  MEDIUM_PERCENTAGE_THRESHOLD,
  MODERATION_NS,
  OW_MODEL_PREFIX,
  SUSPECTED_BOT_ENABLED,
  SUSPECTED_BOT_THRESHOLD,
  TEXT_ANALYZER_SETTINGS
} from './moderationFiltersConstants';
import { createMultipleThresholds } from './createMultipleThresholdsUtil';

export function parseModerationFiltersConfigs(
  configs: ModerationFiltersConfigsResponse
): [ModelConfigs, DataScienceModelMap] {
  const {
    openweb_incivility_thresholds,
    openweb_incivility_tagger_enabled,
    text_analyzer_settings,
    models_config,
    suspected_bot_tagger_threshold,
    suspected_bot_tagger_enabled
  } = configs.tenant.moderation;

  const settings = text_analyzer_settings.value;

  const models: ModelConfigs = {};
  const dataScienceModelsMap: DataScienceModelMap = {};

  const modelsConfig = models_config.value;
  const modelsKeys = Object.keys(modelsConfig);

  if (suspected_bot_tagger_enabled.value) {
    settings[Tag.SUSPECTED_BOT] = [suspected_bot_tagger_threshold.value];
  }

  modelsKeys.forEach(model => {
    const values = settings[model] || null;

    const modelConfigValue = modelsConfig[model];
    const { name, ds_model, hidden } = modelConfigValue;

    if (!hidden) {
      models[model] = {
        label: name || getTagText(model as AnyTag) || convertCase(model),
        value: values ? convertArrayThresholdsToRecord(values) : null
      };
    }

    if (model.startsWith(OW_MODEL_PREFIX)) {
      dataScienceModelsMap[model] = ds_model || '';
    }
  });

  const { value: incivilityThresholds } = openweb_incivility_thresholds;

  if (
    incivilityThresholds &&
    openweb_incivility_tagger_enabled.value &&
    models[ModerationModel.Incivility]
  ) {
    const { block_all, require_approval, publish_and_moderate } = incivilityThresholds;
    const thresholds = createMultipleThresholds(publish_and_moderate, require_approval, block_all);

    models[ModerationModel.Incivility].value = thresholds;
  }

  return [models, dataScienceModelsMap];
}

function convertToInt(threshold: number): number {
  return Math.ceil(threshold * 100);
}

function convertArrayThresholdsToRecord(
  thresholds: Thresholds
): MultipleThresholds | SingleThreshold {
  if (thresholds.length === 1) {
    return {
      type: 'single',
      threshold: convertToInt(thresholds[0])
    };
  }

  const [, publishAndModerate, requireApproval, reject] = thresholds.map(stat =>
    convertToInt(stat)
  );

  return {
    type: 'multiple',
    reject,
    requireApproval,
    publishAndModerate
  };
}

function convertCase(word = ''): string {
  const lower = word.toLowerCase();

  return lower.replace(/(^| )(\w)/g, x => x.toUpperCase()).replace(/_/g, ' ');
}

export function getDefaultThresholds(model: string): SingleThreshold | MultipleThresholds {
  if (model === Tag.SUSPECTED_BOT) {
    return DEFAULT_SINGLE_THRESHOLD;
  }

  if (model === ModerationModel.Incivility) {
    return MEDIUM_NUM_THRESHOLD;
  }

  return MEDIUM_PERCENTAGE_THRESHOLD;
}

export function getModerationKey(key: string): string {
  return `${MODERATION_NS}.${key}`;
}

export function prepareModerationFiltersConfig(
  configs: ModelConfigsValues,
  dsMap: DataScienceModelMap,
  includeUsernameModeration = false
): TenantConfigs {
  const result: TenantConfigs = [];

  const { incivility, suspected_bot, ...regularConfigs } = configs;

  result.push(getTextAnalyzerConfig(regularConfigs));
  result.push(...getIncivilityConfigs(incivility));
  result.push(...getSuspectedBotConfig(suspected_bot));
  result.push(
    getActiveDataScienceModelsMapConfig(Object.keys(configs), dsMap, includeUsernameModeration)
  );

  return result;
}

function toDecimal(value: number): number {
  return value / 100;
}

function getTextAnalyzerConfig(values: ModelConfigsValues): TenantConfigValue {
  const textAnalyzerConfig = {};

  Object.entries(values).forEach(([model, value]) => {
    if (!value) {
      return;
    }

    if (value.type === 'single') {
      textAnalyzerConfig[model] = [toDecimal(value.threshold)];
    }

    if (value.type === 'multiple') {
      const { reject, requireApproval, publishAndModerate } = value;
      textAnalyzerConfig[model] = [0, publishAndModerate, requireApproval, reject].map(toDecimal);
    }
  });

  return { key: getModerationKey(TEXT_ANALYZER_SETTINGS), value: textAnalyzerConfig };
}

function getIncivilityConfigs(value: ModelConfigValue): TenantConfigs {
  const result: TenantConfigs = [];

  const isEnabled = !!value;

  result.push({ key: getModerationKey(INCIVILITY_TAGGER_FEATURE_FLAG), value: isEnabled });

  if (isEnabled && value && value.type === 'multiple') {
    const { reject, requireApproval, publishAndModerate } = value;
    const newValue: IncivilityValue = {
      block_all: reject,
      require_approval: requireApproval,
      publish_and_moderate: publishAndModerate
    };

    result.push({
      key: getModerationKey(INCIVILITY_THRESHOLD_CONFIG),
      value: newValue
    });
  }

  return result;
}

function getSuspectedBotConfig(value: ModelConfigValue): TenantConfigs {
  const result: TenantConfigs = [];

  const isEnabled = !!value;

  result.push({ key: getModerationKey(SUSPECTED_BOT_ENABLED), value: isEnabled });

  if (isEnabled && value && value.type === 'single') {
    const { threshold } = value;

    result.push({
      key: getModerationKey(SUSPECTED_BOT_THRESHOLD),
      value: toDecimal(threshold)
    });
  }

  return result;
}

function getActiveDataScienceModelsMapConfig(
  models: Array<string>,
  dsMap: DataScienceModelMap,
  includeUsernameModeration = false
): TenantConfigValue {
  const value: DataScienceModelMap = {};

  models
    .filter(model => model.startsWith(OW_MODEL_PREFIX))
    .forEach(model => {
      value[model] = dsMap[model];
    });

  if (includeUsernameModeration) {
    value[INAPPROPRIATE_USERNAME_MODEL] = dsMap[INAPPROPRIATE_USERNAME_MODEL];
  }

  return { key: `tenant.moderation-ml.ow_analyzer_models`, value };
}
